<template>
  <div data-testid="reports-and-models" class="w-full">
    <SectionTitle
      title="Reports & Models"
      :url="viewMoreUrl"
      view-more-text="View More"
      :hide-button="props.reports.length === 0"
    />
    <template v-if="props.reports.length">
      <div class="mb-2 overflow-x-hidden">
        <div class="relative flex gap-x-2">
          <Swiper
            data-testid="reports-swiper"
            class="w-full max-w-full xl:grow-0"
            :slides-per-view="slidesPerView"
            :slides-per-group="slidesPerView"
            :space-between="12"
            :loop="false"
            :speed="500"
            @swiper="onSwiper"
            @slide-change="updateNavigationButtons"
          >
            <SwiperSlide data-testid="report-swiper-slide-0">
              <HeroVideoReportCard
                v-if="checkIfIsVideo(featuredReport)"
                :report="featuredReport"
                :category="product"
              />
              <HeroReportCard
                v-else
                :report="featuredReport"
                :category="product"
              />
            </SwiperSlide>
            <SwiperSlide
              v-for="(pair, i) in reportsPairs"
              :key="i"
              :data-testid="`report-swiper-slide-${i + 1}`"
              class="!grid grid-rows-2 gap-3"
            >
              <div v-for="report in pair" :key="report.id">
                <VideoCard
                  v-if="checkIfIsVideo(report)"
                  :report="report"
                  :product="product"
                  :initial-fields="getInitialFields(report)"
                  hide-label
                />
                <ReportCard
                  v-else
                  :report="report"
                  :category="product"
                  :initial-fields="getInitialFields(report)"
                  :show-copy-link="report.market_type === 'prices'"
                />
              </div>
            </SwiperSlide>
          </Swiper>
        </div>
      </div>

      <div class="flex gap-7 text-p-500 mt-4">
        <button
          ref="buttonPrev"
          data-testid="swiper-prev-button"
          :disabled="isFirstSlide || reportsPairs?.length < slidesPerView"
          class="rounded-full border-2 border-white bg-white p-1.5 shadow-md transition-all hover:border-s-400 disabled:opacity-40"
          @click="() => swiperInstance.slidePrev()"
        >
          <IconChevronLeft class="h-4 w-4" />
        </button>
        <button
          ref="buttonNext"
          data-testid="swiper-next-button"
          :disabled="isLastSlide || reportsPairs?.length < slidesPerView"
          class="rounded-full border-2 border-white bg-white p-1.5 shadow-md transition-all hover:border-s-400 disabled:opacity-40"
          @click="() => swiperInstance.slideNext()"
        >
          <IconChevronLeft class="h-4 w-4 rotate-180" />
        </button>
      </div>
    </template>
    <p v-else class="text-center text-black mt-6">
      There are no reports available yet
    </p>
  </div>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'

import HeroReportCard from '~/components/portals/HeroReportCard.vue'
import HeroVideoReportCard from '~/components/portals/HeroVideoReportCard.vue'
import SectionTitle from '~/components/portals/SectionTitle.vue'
import ReportCard from '~/components/shared/market/ReportCard.vue'
import VideoCard from '~/components/shared/market/VideoCard.vue'

import IconChevronLeft from '~/assets/icons/chevron-left.svg'

import { arrayToPairs } from '~/utils/array'

import {
  ESG,
  FORECASTS,
  PRICE_ASSESSMENTS,
} from '~/constants/services/constants'
import { MARKET_TYPE } from '~/src/constants/markets'
import { REPORT_TAG } from '~/src/constants/reports'
import type { TMarketType } from '~/src/types/markets'
import type { TReportCard } from '~/src/types/reports'
import { isReportValidByTaxonomy } from '~/src/utils/reports/filter'

interface Props {
  product: string
  category?: string | null
  reports: TReportCard[]
}

const props = withDefaults(defineProps<Props>(), {
  category: null,
})

const slidesPerView = ref(2)
const isFirstSlide = ref(true)
const isLastSlide = ref(false)
const swiperInstance = ref(null)

const viewMoreUrl = computed(() => `/${props.product}/data-reports`)
const featuredReport = computed(() => {
  const [firstReport] = props.reports
  return {
    ...firstReport,
    show_label: firstReport?.market_type === MARKET_TYPE.PRICES_ASSESSMENTS,
  }
})
const reportsPairs = computed(() => {
  return arrayToPairs([...props.reports].slice(1))
})

const onSwiper = (swiper) => {
  swiperInstance.value = swiper
}

const updateNavigationButtons = () => {
  isFirstSlide.value = swiperInstance.value.isBeginning
  isLastSlide.value = swiperInstance.value.isEnd
}

const initializeBreakpoints = () => {
  const mql = window.matchMedia('(max-width: 1420px)')
  const mqlMobile = window.matchMedia('(max-width: 720px)')

  if (!mql.matches) {
    slidesPerView.value = 3
  } else if (mqlMobile.matches) {
    slidesPerView.value = 1
  } else {
    slidesPerView.value = 2
  }

  mql.onchange = (e) => {
    if (e.matches) {
      slidesPerView.value = 2
    } else {
      slidesPerView.value = 3
    }
  }

  mqlMobile.onchange = (e) => {
    if (e.matches) {
      slidesPerView.value = 1
    } else {
      slidesPerView.value = 2
    }
  }
}

const checkIfIsVideo = (report: TReportCard) => {
  if (!report.tags) return false
  return (
    report?.tags[0]?.name === 'forecast-video' ||
    report?.tags[0]?.name === 'esg-video'
  )
}

const getServiceByMarketType = (marketType: TMarketType) => {
  if (marketType === MARKET_TYPE.FORECASTS) return FORECASTS.checkbox
  if (marketType === MARKET_TYPE.SUSTAINABILITY) return ESG.slug
  return PRICE_ASSESSMENTS.slug
}

const getInitialFields = (report: TReportCard) => {
  const isVideo = checkIfIsVideo(report)

  if (isVideo) {
    return [
      props.product,
      isReportValidByTaxonomy(report, REPORT_TAG.FORECASTS_VIDEO)
        ? FORECASTS.checkbox
        : ESG.slug,
    ]
  }

  const service = getServiceByMarketType(report?.market_type)
  return [props.category, service]
}

onMounted(async () => {
  initializeBreakpoints()
})
</script>
